/* ============
 * Main File
 * ============
 *
 * Will initialize the application.
 */

/* ============
 * Plugins
 * ============
 *
 * Import and bootstrap the plugins.
 */
import 'normalize-stylus'
import './plugins/vue-speech.js'

import { langList as acceptedLangs } from '@2l/locale/'
/* ============
 * Main App
 * ============
 *
 * Last but not least, we import the main application.
 */
import { G } from '@2l/utils'
import { PiniaDebounce } from '@pinia/plugin-debounce'
import * as Sentry from '@sentry/vue'
import { createHead } from '@vueuse/head'
import { createPinia } from 'pinia'
import persistedstate from 'pinia-plugin-persistedstate'
import { debounce } from 'ts-debounce'
import { createApp, markRaw } from 'vue'

import { CURRENCIES_LIST } from '@/composables/types'
import { authorizeBySessionRoute } from '@/composables/useAutoAuth'
import { STORAGE_KEYS } from '@/constants/storageKeys'

import App from './App.vue'
import components from './components/UiComponents'
import { setApiContext } from './plugins/apiClient'
import any from './plugins/vue-any'
import { i18n } from './plugins/vue-i18n.js'
import paddlePlugin from './plugins/vue-paddle'
import { router } from './plugins/vue-router.js'
import { installWebVitals } from './plugins/vue-webvitals'
import store from './store'

const app = createApp(App)
const pinia = createPinia()
pinia.use(PiniaDebounce(debounce))
pinia.use(persistedstate)

const dsn = __SENTRY_DSN__
const release = __SENTRY_RELEASE__ || `ewa-class@${__VERSION__}`
const environment = __ENVIRONMENT__ || 'development'
const getMessage = G('originalException.message')
const sentryRegexp =
  /database deleted|firebaseError|remote Config|createObjectStore|database connection is closing/gi

const head = createHead()

Sentry.init({
  app,
  dsn,
  release,
  environment,
  beforeSend(event, hint) {
    const message = getMessage(hint)
    if (message && message.match(sentryRegexp)) {
      return null
    }

    return __IS_LOCAL_DEVELOPMENT__ === 'yes' ? null : event
  },

  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Firebase
    /firebase\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    /mc\.yandex\.ru/i,
    /twsesi\.appewa\.com/i
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
})

components.forEach((component) => {
  app.component(`${component.name}`, component)
})

import('@sentry/vue').then((lazyLoadedSentry) => {
  Sentry.addIntegration(lazyLoadedSentry.replayIntegration())
})

app.performance = __ENVIRONMENT__ !== 'production'

const triggerExperimentLayer = async function (experiment_layer, experimentId) {
  await app.config.globalProperties.$triggerExperimentLayer(
    experiment_layer,
    experimentId
  )
}

const isLangAvailable = (lang) => acceptedLangs.includes(lang)

const checkExperimentLayer = () => {
  const isaAuth = store.getters['auth/isAuthenticated']
  if (isaAuth) {
    return ['external']
  }

  return ['conversion', 'external']
}

let targetRoute = null

router.beforeEach(async (to, _from) => {
  targetRoute = to
  try {
    if (!store.getters['account/getUserId'] && (to.meta.auth || store.getters['auth/isAuthenticated'])) {
      await store.dispatch('account/find')
    }

    const language = to?.query.language
    const experimentId = to?.meta?.experimentId || ''
    const experimentLayer =
      to?.meta?.experiment_layer === 'conversion'
        ? checkExperimentLayer(to?.meta?.experiment_layer)
        : to?.meta?.experiment_layer

    await triggerExperimentLayer(experimentLayer, experimentId)

    if (!store.getters['auth/isAuthenticated']) {
      if (isLangAvailable(language))
        await store.dispatch('account/setLang', language)

      await store.dispatch('account/detectLang')
    }

    // to bypass unauthenticated and premium user users always
    if (to?.meta?.promo || !to?.meta?.auth) {
      return
    }

    if (!store.getters['account/getUserId']) {
      await store.dispatch('account/find')
    }

    const hasPremium = store.getters['account/isPremium']
    if (hasPremium) {
      return
    }

    const userId = store.getters['account/getUserId']

    if (to.meta.auth && !userId) {
      sessionStorage.setItem(STORAGE_KEYS.AUTH_REDIRECT, to.fullPath)
      return { name: 'auth.index' }
    }
  } finally {
    targetRoute = null
  }
})

pinia.use(({ store: piniaStore }) => {
  piniaStore.$router = markRaw(router)
})

installWebVitals(app, router)

setApiContext({
  getRequestHeadersData: () => {
    const acceptLanguage =
      store.getters['account/getActiveProfile'] ||
      store.getters['account/getCurrentLanguage']
    const xSystemLanguage = store.getters['account/getCurrentLanguage']
    const userCountryCode = store.getters['account/getUserCountry']
    const currency =
      userCountryCode && CURRENCIES_LIST[userCountryCode]
        ? CURRENCIES_LIST[userCountryCode].currency
        : undefined
  
    return { acceptLanguage, xSystemLanguage, currency }
  },
  onUnauthorized: async () => {
    const route = targetRoute ?? router.currentRoute.value
    store.commit('auth/LOGOUT')

    const isAuthRequired = route?.meta?.auth === true

    if (route?.meta?.allowSessionAuth === true && !isAuthRequired) {
      try {
        await authorizeBySessionRoute(route, store)
      } catch {
        store.commit('auth/LOGOUT')
      } finally {
        router.push({ name: route.name })
      }
      return
    }

    if (isAuthRequired) {
      sessionStorage.setItem(STORAGE_KEYS.AUTH_REDIRECT, route.fullPath)
      router.push({ name: 'auth.index' })
      return  
    }
  
    router.push({ name: route.name })
  },
  onAccessForbidden: () => {
    router.push({ name: 'offers.index' })
  }
})

app
  .use(any)
  .use(router)
  .use(store)
  .use(pinia)
  .use(i18n)
  .use(head)
  .use(paddlePlugin)

app.mount('#app')
